import { memo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Search, ChevronDown, Zap, ShoppingBag, Package, Users, HelpCircle, Mail, BookOpen, Sparkles, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CartDrawer } from "./CartDrawer";
import { SearchModal } from "./SearchModal";
import vapebolagLogo from "@/assets/vapebolag-logo.webp";
import ageWarningBadge from "@/assets/age-warning.svg";
import postnordLogo from "@/assets/postnord-logo.png";
import revolutLogo from "@/assets/revolut-logo.jpg";

// P0 Performance: Removed framer-motion AnimatePresence - use CSS transitions

const shopLinks = [
  { name: "THCA Vapes", href: "/kollektioner/thca-vapes", icon: Zap, description: "99% ren THCA", hot: true },
  { name: "Buds", href: "/kollektioner/thca-buds", icon: Sparkles, description: "Premium blommor" },
  { name: "Hash", href: "/kollektioner/thca-hash", icon: Package, description: "Traditionell hash" },
  { name: "Bundles", href: "/kollektioner/bundles", icon: ShoppingBag, description: "Spara mer" },
];

const infoLinks = [
  { name: "Artiklar & Guider", href: "/blogg", icon: BookOpen },
  { name: "Vanliga frågor", href: "/faq", icon: HelpCircle },
  { name: "Om oss", href: "/om-oss", icon: Users },
  { name: "Kontakt", href: "/kontakt", icon: Mail },
];

interface HeaderProps {
  onSearchClick?: () => void;
}

export const Header = memo(({ onSearchClick }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      setSearchOpen(true);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        handleSearchClick();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSearchClick]);

  return (
    <>
      {/* Announcement bar - simple text only */}
      <div
        className={`cta-gradient text-center text-sm font-bold overflow-hidden transition-all duration-200 ${
          isScrolled ? "h-0 py-0 opacity-0" : "h-auto py-2.5 opacity-100"
        }`}
      >
        <span className="hidden sm:inline">🚀 </span>
        Fri frakt över 499 kr • Leverans 1-3 dagar • 100% Lagligt
        <span className="hidden sm:inline"> 🚀</span>
      </div>

      <header 
        className={`sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border transition-all duration-200 ${
          isScrolled ? "shadow-md" : ""
        }`}
      >
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-200 ${
            isScrolled ? "h-14" : "h-16"
          }`}>
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <img 
                src={vapebolagLogo}
                alt="Vapebolag – Köp THCA vape, hash och buds i Sverige"
                className={`w-auto transition-all duration-200 ${isScrolled ? "h-9" : "h-11"}`}
                width="44"
                height="44"
              />
            </Link>

            {/* Desktop navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {/* Shop dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShopDropdownOpen(!shopDropdownOpen)}
                  onBlur={() => setTimeout(() => setShopDropdownOpen(false), 200)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-foreground hover:text-primary transition-colors rounded-xl hover:bg-secondary"
                >
                  Butik
                  <ChevronDown className={`w-4 h-4 transition-transform ${shopDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown - CSS transition */}
                {shopDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-72 py-3 bg-card rounded-2xl border border-border shadow-xl z-50 animate-scale-in">
                    {shopLinks.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center gap-4 px-4 py-3 hover:bg-secondary transition-colors group mx-2 rounded-xl"
                      >
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-accent/30 transition-colors">
                          <item.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="block font-bold text-foreground group-hover:text-primary transition-colors">
                              {item.name}
                            </span>
                            {item.hot && (
                              <span className="px-2 py-0.5 text-[10px] font-bold cta-gradient rounded-full">
                                HOT
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{item.description}</span>
                        </div>
                      </Link>
                    ))}
                    <div className="border-t border-border/50 mt-3 pt-3 mx-4">
                      <Link
                        to="/kollektioner/alla-produkter"
                        className="flex items-center gap-2 py-2 text-sm font-bold text-cta hover:text-cta-hover transition-colors"
                      >
                        Se alla produkter →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link 
                to="/blogg" 
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors rounded-xl hover:bg-secondary"
              >
                Artiklar
              </Link>
              
              <Link 
                to="/om-oss" 
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors rounded-xl hover:bg-secondary"
              >
                Om oss
              </Link>
              
              <Link 
                to="/faq" 
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors rounded-xl hover:bg-secondary"
              >
                FAQ
              </Link>
              
              <Link 
                to="/kontakt" 
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors rounded-xl hover:bg-secondary"
              >
                Kontakt
              </Link>
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-2">

              {/* Search button with keyboard hint */}
              <Button 
                variant="ghost" 
                size="icon" 
                aria-label="Sök (⌘K)"
                className="text-muted-foreground hover:text-primary hover:bg-secondary rounded-xl relative"
                onClick={handleSearchClick}
              >
                <Search className="w-5 h-5" />
              </Button>
              
              <Link to="/konto" aria-label="Mitt konto" className="hidden sm:block">
                <Button variant="ghost" size="icon" aria-label="Mitt konto" className="text-muted-foreground hover:text-primary hover:bg-secondary rounded-xl">
                  <User className="w-5 h-5" />
                </Button>
              </Link>
              
              <CartDrawer />
              
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden hover:bg-secondary rounded-xl"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Stäng meny" : "Öppna meny"}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu - CSS transition */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-card animate-fade-in">
            <div className="container mx-auto px-4 py-6">
              {/* Shop section */}
              <div className="mb-6">
                <span className="text-xs font-bold text-primary uppercase tracking-wider mb-3 block">
                  Butik
                </span>
                <div className="grid grid-cols-2 gap-3">
                  {shopLinks.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex flex-col items-center gap-2 py-4 px-4 text-foreground font-medium bg-secondary rounded-2xl border border-border hover:border-primary/50 transition-all active:scale-95"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                        <item.icon className="w-6 h-6 text-primary" />
                      </div>
                      <span className="text-sm">{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="border-t border-border/50 pt-6">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 block">
                  Information
                </span>
                <div className="space-y-1">
                  {infoLinks.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 py-3 px-4 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-xl transition-colors"
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Search Modal (internal state) */}
      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
});

Header.displayName = "Header";
